/**
 * AppLayout Component
 *
 * Shared layout component that wraps SideMenu + main content area.
 * Used by LandingPage, ProjectList, ProjectDetails, and MyProfile templates.
 *
 * @module @ideoz/ui/components/layouts/AppLayout
 */

import React, { useState, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../atoms/utils';
import { SideMenu } from '../molecules/SideMenu';
import { AnimatedBanner } from '../organisms/AnimatedBanner';
import { useSubscriptionExpiration } from '@ideoz/core';
import { isWebBuild } from '@ideoz/core';
import type {
  NavigationItem,
  SocialLink,
  User,
  UserMenuItem,
  UserSocialLink,
} from '../../types/SideMenu.types';

export interface AppLayoutProps {
  /** Main content to render */
  children: React.ReactNode;
  /** Current path for sidebar active state */
  currentPath?: string;
  /** Sidebar configuration */
  sidebar: {
    navigationItems: NavigationItem[];
    socialLinks?: SocialLink[];
    /** Optional: navigate to landing on logo click */
    onNavigateToLanding?: () => void;
  };
  /** User information */
  user: User;
  /** User menu configuration (shown when logged in) */
  userMenuProps?: {
    points?: number;
    pointsLabel?: string;
    menuItems: UserMenuItem[];
    socialLinks?: UserSocialLink[];
    onRequestUpgrade?: () => void;
    isRequestingUpgrade?: boolean;
    canRequestUpgrade?: boolean;
    isWeb: boolean;
  };
  /** Whether rendering for web platform */
  isWeb: boolean;
  /** Test ID for sidebar */
  testId?: string;
  /** Additional className for the root container */
  className?: string;
  /** Additional className for the main content area */
  mainClassName?: string;
  /** Height style - defaults to 'screen' */
  height?: 'screen' | 'full' | 'auto';
  /** Whether sidebar starts collapsed. Defaults to true. Pass false to start expanded. */
  defaultSidebarCollapsed?: boolean;
  /** Optional: Override subscription expiration banner configuration */
  subscriptionExpirationBanner?: {
    /** Whether to show the banner (overrides Redux data) */
    isVisible?: boolean;
    /** Number of days until expiration (overrides Redux data) */
    daysUntilExpiration?: string;
    /** Handler for renew action (overrides default navigation to billing) */
    onRenew?: () => void;
    /** Handler for close banner */
    onClose?: () => void;
  };
  /** Ref for main scrollable area */
  mainScrollRef?: React.Ref<HTMLDivElement>;
}

/**
 * AppLayout provides a consistent sidebar + main content layout.
 * Manages sidebar collapsed state internally.
 *
 * @example
 * ```tsx
 * <AppLayout
 *   currentPath="/projects"
 *   sidebar={facade.sidebar}
 *   user={{ isLoggedin: true, name: 'John' }}
 *   userMenuProps={userMenuConfig}
 *   isWeb={true}
 *   testId="projects-layout"
 * >
 *   <YourContent />
 * </AppLayout>
 * ```
 */
export function AppLayout({
  children,
  currentPath,
  sidebar,
  user,
  userMenuProps,
  isWeb,
  testId,
  className,
  mainClassName,
  height = 'screen',
  defaultSidebarCollapsed = true,
  subscriptionExpirationBanner: overrideBanner,
  mainScrollRef,
}: AppLayoutProps): JSX.Element {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(defaultSidebarCollapsed);

  // Read subscription expiration from Redux (appears on all pages)
  const subscriptionExpiration = useSubscriptionExpiration();

  // Default renew handler: navigate to billing page
  const handleRenew = useCallback(() => {
    if (overrideBanner?.onRenew) {
      overrideBanner.onRenew();
    } else {
      // Default: navigate to billing page
      if (isWebBuild()) {
        // Web: use window.location
        window.location.href = '/billing';
      } else {
        // Plugin: use postMessage or other navigation method
        // For now, just log - plugin navigation should be handled by parent
        console.log('Navigate to billing (plugin)');
      }
    }
  }, [overrideBanner?.onRenew]);

  // Use override banner data if provided, otherwise use Redux data
  const bannerData = overrideBanner
    ? {
        isVisible: overrideBanner.isVisible ?? subscriptionExpiration.isExpiringWithin3Days,
        daysUntilExpiration:
          overrideBanner.daysUntilExpiration ?? subscriptionExpiration.daysUntilExpiration,
        onRenew: handleRenew,
        onClose: overrideBanner.onClose,
      }
    : {
        isVisible: subscriptionExpiration.isExpiringWithin3Days,
        daysUntilExpiration: subscriptionExpiration.daysUntilExpiration,
        onRenew: handleRenew,
        onClose: undefined,
      };

  const heightClass = {
    screen: 'min-h-screen',
    full: 'h-[100vh]',
    auto: 'h-auto',
  }[height];

  return (
    <div
      className={cn(
        heightClass,
        'flex',
        'bg-background text-gray-900 dark:text-gray-100',
        className
      )}
      style={
        {
          '--sidebar-width': isSidebarCollapsed
            ? 'var(--sidebar-width-collapsed)'
            : 'var(--sidebar-width-expanded)',
        } as React.CSSProperties
      }
    >
      <SideMenu
        items={sidebar.navigationItems}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        currentPath={currentPath}
        socialLinks={sidebar.socialLinks}
        onLogoClick={sidebar.onNavigateToLanding}
        user={user}
        userMenuProps={userMenuProps}
        testId={testId}
      />

      <main
        className={cn(
          'flex-1 overflow-auto transition-[margin-inline-start] duration-200 ease-in-out',
          'ms-[var(--sidebar-width)]',
          mainClassName
        )}
        ref={mainScrollRef}
      >
        {/* Subscription Expiration Banner - Global (reads from Redux) */}
        {bannerData.isVisible && bannerData.daysUntilExpiration && (
          <AnimatedBanner
            className="relative left-0 right-0 mx-auto mb-4 top-3 me-2"
            isVisible={bannerData.isVisible}
            icon={AlertCircle}
            text={`Your subscription will end in ${bannerData.daysUntilExpiration}. Continue solving the right UX problems, the right way.`}
            actionText=""
            onAction={bannerData.onRenew}
            onClose={bannerData.onClose}
            showCloseButton={false}
            variant={isWeb ? 'web' : 'plugin'}
            testId="subscription-expiration-banner"
          />
        )}
        {children}
      </main>
    </div>
  );
}

export default AppLayout;
